import { NotesEntityItem } from '@components/notesDialog/types';
import { Box, Typography } from '@mui/material';
import { fakeLocalMidnightFromTZMidnight, ShortDateFormat } from '@utils/date';
import { format } from 'date-fns-tz';

export interface NotesEntitiesSideMenuProps {
  items: NotesEntityItem[];
  selectedId: string | null;
  onSelect: (item: NotesEntityItem) => void;
  emptyLabel?: string;
  id?: string;
}

const formatMaybeDate = (d?: string | Date | null) => {
  if (!d) return '';
  const dateObj = typeof d === 'string' ? new Date(d) : d;
  if (!dateObj || isNaN(dateObj.getTime())) return '';
  return format(fakeLocalMidnightFromTZMidnight(dateObj), ShortDateFormat);
};

export const NotesEntitiesSideMenu = ({
  items,
  selectedId,
  onSelect,
  emptyLabel = 'No items',
  id = 'notes-entities-side-menu',
}: NotesEntitiesSideMenuProps) => {
  return (
    <Box id={id} sx={sx.container}>
      {items.map((item) => {
        const active = item.id === selectedId;
        const effective = formatMaybeDate(item.effectiveOn);
        const expires = formatMaybeDate(item.expiresOn);

        const secondary = effective ? (expires ? `${effective} - ${expires}` : `Effective: ${effective}`) : '';

        return (
          <Box key={item.id} role='button' tabIndex={0} onClick={() => onSelect(item)} sx={sx.row(active)}>
            {item.title}
            {secondary && (
              <Box component='span' sx={sx.secondaryLine(active)}>
                <Typography variant='caption' sx={{ color: 'inherit' }}>
                  {secondary}
                </Typography>
              </Box>
            )}
            {item.metaLabels &&
              item.metaLabels.map((label, index) => (
                <Box component='span' sx={sx.secondaryLine(active)} key={index}>
                  <Typography variant='caption' sx={{ color: 'inherit' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
          </Box>
        );
      })}
      {!items.length && <Box sx={{ fontSize: 12, opacity: 0.6, p: 1 }}>{emptyLabel}</Box>}
    </Box>
  );
};

const sx = {
  container: {
    borderRight: '1px solid black',
    borderColor: 'border.main',
    width: { xs: 160, sm: 220, md: 260 },
    minWidth: { xs: 160, sm: 200, md: 260 },
    p: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
    overflowY: 'auto',
  },
  row: (active: boolean) => ({
    px: 1,
    py: 0.75,
    cursor: 'pointer',
    fontSize: 13,
    bgcolor: active ? 'primary.main' : 'transparent',
    color: active ? 'primary.contrastText' : 'inherit',
    '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' },
  }),
  secondaryLine: (active: boolean) => ({
    display: 'block',
    fontSize: 11,
    opacity: 0.75,
    mt: 0.25,
    color: active ? 'primary.contrastText' : 'inherit',
    lineHeight: 1.1,
  }),
};
